<script setup lang="ts">
import { useTheme } from './composables/useTheme';
import { useAuthStore } from './stores/auth';
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue';
import { useRoute } from 'vue-router';

import Header from './layouts/Header.vue';
import MainNav from './layouts/MainNav.vue';
import Sidebar from './layouts/Sidebar.vue';
import NotificationHost from './components/ui/NotificationHost.vue';
import { useHeartbeat } from './composables/useHeartbeat';

// Mobile Warning State
const showMobileWarning = ref(false);

// Mobile Warning Functions
const checkMobileWarning = () => {
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    showMobileWarning.value = true;
  }
};

const closeMobileWarning = () => {
  showMobileWarning.value = false;
};

// Debug-Komponenten wurden entfernt - nur noch Core-Komponenten

// Stores und Route initialisieren
const authStore = useAuthStore();
const route = useRoute();

// Auth store wird automatisch über Pinia initialisiert
console.log('[App] Auth store bereit');

// Heartbeat für Online-User-Tracking starten (sendet alle 30s wenn authentifiziert)
useHeartbeat();

// Initialisiere das Theming-System beim App-Start
useTheme();

// Scroll-Variablen - vereinfachte Logik mit Animation-Lock
const lastScrollPosition = ref(0);
const isHeaderVisible = ref(true);
const isAnimating = ref(false); // Lock während Animation
const scrollTolerance = 20; // Mindestbewegung um Status zu ändern (erhöht)
const minScrollToHide = 120; // Minimum Scroll-Position bevor Header versteckt werden kann
const animationDuration = 300; // CSS-Animation-Dauer in ms

// Sidebar-Status - Initialisierung mit gespeichertem Wert
const isSidebarCollapsed = ref(false);

// Sidebar-Status sofort beim Script-Setup laden (vor erstem Render)
function initializeSidebarState() {
  try {
    const uiSettings = JSON.parse(localStorage.getItem('uiSettings') || '{}');
    if (uiSettings.sidebarCollapsed !== undefined) {
      return uiSettings.sidebarCollapsed;
    } else {
      // Fallback auf ältere Speicherform
      const savedState = localStorage.getItem('sidebarCollapsed');
      if (savedState !== null) {
        return JSON.parse(savedState);
      }
    }
  } catch (error) {
    console.error('Fehler beim Laden des Sidebar-Status:', error);
  }
  return false; // Standard-Wert
}

// Sidebar-Status sofort initialisieren
isSidebarCollapsed.value = initializeSidebarState();

// Prüfen, ob die aktuelle Route die Startseite ist (inklusive Callback für Home-Layout)
const isHomePage = computed(() => route.path === '/' || route.path === '/auth/callback');

// Handler für Sidebar-Collapse-Event
function handleSidebarCollapse(collapsed: boolean) {
  isSidebarCollapsed.value = collapsed;
  // Debug-Information in der Konsole ausgeben
  console.log(`Sidebar-Status geändert: ${collapsed ? 'Eingeklappt' : 'Ausgeklappt'}`);
  // CSS-Klassen auf dem page-wrapper prüfen
  console.log('page-wrapper Klassen:', document.querySelector('.page-wrapper')?.className);
}

// Keine Debug-Variablen mehr benötigt

// Debug-Funktionen entfernt

// Bei Änderung des isHeaderVisible-Status CSS-Variable und data-Attribut aktualisieren
watch(isHeaderVisible, (newValue: boolean) => {
  document.documentElement.style.setProperty('--header-state', newValue ? 'visible' : 'hidden');
  document.documentElement.setAttribute('data-header-state', newValue ? 'visible' : 'hidden');
  
  // MainNav-Position dynamisch anpassen
  document.documentElement.style.setProperty('--main-nav-top', newValue ? 'var(--header-height)' : '0');
  
  // Dynamische Höhe je nach Header-Status setzen (ohne Padding)
  document.documentElement.style.setProperty('--visible-header-height', 
    newValue ? 'calc(var(--header-content-height) + var(--nav-height))' : 'var(--nav-height)');
});

// isHeaderVisible-Variable für Komponenten bereitstellen
provide('isHeaderVisible', isHeaderVisible);

// Vereinfachter Scroll-Handler mit Animation-Lock gegen Flackern
function handleScroll(event?: Event) {
  // Das main-content Element als Scroll-Container verwenden
  const mainContent = event?.target as HTMLElement || document.querySelector('.main-content');
  if (!mainContent) return;
  
  // Aktuelle Scroll-Position vom main-content Element ermitteln
  const currentScrollPosition = mainContent.scrollTop || 0;
  
  // Berechne die Scroll-Distanz seit letzter Änderung
  const scrollDifference = currentScrollPosition - lastScrollPosition.value;
  
  // KRITISCH: Während Animation KEINE Statusänderungen erlauben
  if (isAnimating.value) {
    // Position tracken, aber Status NICHT ändern
    lastScrollPosition.value = currentScrollPosition;
    return;
  }
  
  // Ignoriere zu kleine Bewegungen (Jitter-Schutz)
  if (Math.abs(scrollDifference) < scrollTolerance) {
    return;
  }
  
  // Einfache Regel 1: Ganz oben (< 10px) → Header IMMER sichtbar
  if (currentScrollPosition < 10) {
    if (!isHeaderVisible.value) {
      setHeaderVisibility(true);
    }
    lastScrollPosition.value = currentScrollPosition;
    return;
  }
  
  // Einfache Regel 2: Zu wenig gescrollt → Header bleibt sichtbar
  if (currentScrollPosition < minScrollToHide) {
    if (!isHeaderVisible.value) {
      setHeaderVisibility(true);
    }
    lastScrollPosition.value = currentScrollPosition;
    return;
  }
  
  // Einfache Regel 3: Nach UNTEN scrollen → Header ausblenden
  if (scrollDifference > 0 && isHeaderVisible.value) {
    setHeaderVisibility(false);
  }
  
  // Einfache Regel 4: Nach OBEN scrollen → Header einblenden
  if (scrollDifference < 0 && !isHeaderVisible.value) {
    setHeaderVisibility(true);
  }
  
  // Position speichern
  lastScrollPosition.value = currentScrollPosition;
}

// Hilfsfunktion: Header-Sichtbarkeit mit Animation-Lock setzen
function setHeaderVisibility(visible: boolean) {
  isHeaderVisible.value = visible;
  isAnimating.value = true;
  
  // Animation-Lock nach CSS-Transition-Duration aufheben
  setTimeout(() => {
    isAnimating.value = false;
  }, animationDuration);
}

// Event-Handler - direkt ohne Debouncing für sofortige Reaktion
function optimizedScrollHandler(event: Event) {
  handleScroll(event);
}

// loadSidebarState() entfernt - wird jetzt sofort beim Script-Setup initialisiert

// Beim App-Start die Authentifizierung überprüfen, Sidebar-Status laden und Scroll-Event hinzufügen
onMounted(async () => {
  try {
    console.log('[App] Starte Auth-Check nach App-Mount');
    
    // Auth store direkt verwenden (Pinia-basiert)
    console.log('[App] Auth store bereit, prüfe Auth-Status');
    
    await authStore.restoreAuthState();
    // Sidebar-Status wird jetzt sofort beim Script-Setup geladen, nicht mehr hier
    
    // Mobile Warning prüfen
    checkMobileWarning();
  } catch (error) {
    console.error('Fehler bei der Authentifizierungsüberprüfung:', error);
  }
  
  // Warten, bis das DOM vollständig geladen ist
  setTimeout(() => {
    // Das main-content Element als Scroll-Container verwenden
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      // Nur auf dem main-content Element Scroll-Events registrieren
      mainContent.addEventListener('scroll', optimizedScrollHandler, { passive: true });
      
      // Für Touch-Geräte
      mainContent.addEventListener('touchmove', optimizedScrollHandler, { passive: true });
      
      // Initial Scroll-Position prüfen
      handleScroll();
      
      console.log('Scroll-Event-Listener auf main-content registriert');
    } else {
      console.error('main-content Element nicht gefunden');
    }
  }, 100);
});

// Event-Listener entfernen, wenn die Komponente unmounted wird
onUnmounted(() => {
  // Event-Listener vom main-content Element entfernen
  const mainContent = document.querySelector('.main-content');
  if (mainContent) {
    mainContent.removeEventListener('scroll', optimizedScrollHandler);
    mainContent.removeEventListener('touchmove', optimizedScrollHandler);
  }
});
</script>

<template>
  <div class="app-container" @scroll="optimizedScrollHandler">
    <!-- Header-Container als eigenständiges Element auf oberster Ebene -->
    <div class="header-container" id="header" :class="{ 'header-hidden': !isHeaderVisible }">
      <Header />
    </div>
    
    <!-- Navigation als eigenständiges Element direkt nach dem Header -->
    <div class="main-nav-container">
      <MainNav />
    </div>
    
    <!-- page-wrapper als Container für Sidebar und main-content -->
    <div class="page-wrapper" :class="{ 
      'header-hidden-state': !isHeaderVisible,
      'sidebar-collapsed': isSidebarCollapsed
    }">
      <!-- Sidebar - nur anzeigen, wenn nicht auf der Startseite -->
      <div class="sidebar-wrapper" v-if="!isHomePage" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
        <Sidebar @update:collapsed="handleSidebarCollapse" />
      </div>
      
      <!-- Hauptinhalt -->
      <main class="main-content" :class="{ 'home-page': isHomePage }">
        <!-- Sentinel-Element für Header-Visibility -->
        <div id="header-sentinel"></div>
        <RouterView />
      </main>
    </div>
    
    <!-- Debug-Komponenten - immer anzeigen -->
    <!-- ApiDebugPanel ausgeblendet -->
    <!-- ApiEndpointTester removed for production -->
    <NotificationHost />
    
    <!-- Mobile Warning Popup -->
    <div v-if="showMobileWarning" class="mobile-warning-overlay">
      <div class="mobile-warning-modal">
        <div class="mobile-warning-content">
          <div class="warning-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          
          <h3 class="warning-title">Achtung</h3>
          
          <p class="warning-message">
            OdA Tools ist nicht für Mobile optimiert. Die Darstellung kann verschoben sein.
          </p>
          
          <div class="warning-actions">
            <button @click="closeMobileWarning" class="warning-button">
              Ok
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Globale Stile - werden auf alle Komponenten angewendet */
:root {
  /* Alle Layout-Dimensionen werden jetzt zentral in variables.css definiert */
  /* Hier nur noch Theme-spezifische Variablen */
  --shadow-color: var(--color-shadow-color, rgba(0, 0, 0, 0.1)); /* Schatten-Farbe aus dem Theme */
}

/* Reset für alle Elemente */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Scrollbar-Stile für ein konsistentes Erscheinungsbild */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--color-background-muted);
}

::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-border-hover);
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  font-family: var(--font-family);
  background-color: var(--color-background);
  color: var(--color-text);
  transition: background-color 0.3s, color 0.3s;
  overflow-x: hidden; /* Verhindert horizontales Scrollen */
}

/* Explizit margin-top für body im Dark-Mode auf 0 setzen */
body.mode-dark {
  margin-top: 0 !important;
}

/* App-Container - umschließt die gesamte Anwendung */
.app-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh; /* Verwende viewport height für konsistente Höhe */
  max-width: 100%; /* Verhindert horizontales Scrollen */
  position: relative;
  overflow: hidden !important; /* Verhindert globales Scrollen */
  background-color: var(--color-background);
  color: var(--color-text);
  transition: background-color 0.3s, color 0.3s;
}

/* Header-Container mit Animation - volle Breite */
.header-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: var(--color-background);
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out, height 0.3s ease-in-out, background-color 0.3s;
  box-shadow: 0 2px 10px var(--shadow-color);
  will-change: transform, opacity, height; /* Optimierung für Hardware-Beschleunigung */
  z-index: 100;
  height: var(--header-content-height);
}

/* Navigation-Container - bleibt sichtbar, wenn Header ausgeblendet ist */
.main-nav-container {
  position: fixed;
  top: var(--header-content-height); /* Feste Position direkt unter dem Header-Content */
  left: 0;
  right: 0;
  width: 100%;
  z-index: 90;
  height: var(--nav-height);
  /* Synchronisierte Transition mit Sidebar */
  transition: top var(--sidebar-transition-duration) ease;
  background-color: var(--color-background);
}

/* CSS-Variablen für Header-Zustand */
:root {
  --header-state: visible;
}

/* CSS-Selektor für Navigation-Position bei ausgeblendetem Header */
.header-container.header-hidden ~ .main-nav-container {
  top: 0;
}

/* Header-Hidden-Klasse mit höherer Spezifität */
.header-container.header-hidden {
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
  height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
  transition: transform 0.3s ease-out, opacity 0.2s ease-out, height 0.3s ease-out;
}

/* Hauptlayout mit Sidebar und Content */
.page-wrapper {
  display: flex;
  flex: 1;
  width: 100%;
  position: fixed; /* Fixierte Position relativ zum Viewport */
  top: var(--visible-header-height); /* Position vom oberen Rand des Viewports */
  left: 0;
  right: 0;
  bottom: 0; /* Ausdehnung bis zum unteren Rand des Viewports */
  height: auto; /* Höhe wird durch top und bottom bestimmt */
  transition: top 0.3s ease, background-color 0.3s;
  background-color: var(--color-background);
  overflow: hidden;
}

/* Hauptinhalt - Flexbox Layout: automatisch neben Sidebar positioniert */
.main-content {
  padding: var(--spacing-md);
  /* Keine width/margin-left Berechnungen mehr - Flexbox regelt das automatisch */
  flex: 1; /* Nimmt automatisch den restlichen Platz neben der Sidebar ein */
  min-width: 0; /* Verhindert Flex-Item-Overflow */
  /* Synchronisierte Transitions mit Sidebar und Main-Nav */
  transition: background-color var(--sidebar-transition-duration) ease, 
              color var(--sidebar-transition-duration) ease;
  background-color: var(--color-background);
  color: var(--color-text);
  overflow-y: auto; /* Erlaubt vertikales Scrollen nur im Hauptinhalt */
  height: 100%;
  position: relative;
  display: flex; /* Flexbox für die Zentrierung des Inhalts */
  flex-direction: column; /* Spaltenausrichtung für den Inhalt */
  align-items: center; /* Horizontale Zentrierung */
}

/* Sidebar-Wrapper - Flexbox Layout */
.sidebar-wrapper {
  height: 100%;
  min-height: 100%; /* Mindestens volle Höhe */
  flex-shrink: 0; /* Sidebar soll nicht schrumpfen */
  display: flex; /* Flex-Container für die Sidebar */
  flex-direction: column; /* Vertikale Ausrichtung */
  /* Breite wird automatisch von der Sidebar-Komponente bestimmt */
}

/* Keine speziellen CSS-Regeln für eingeklappte Sidebar mehr nötig */
/* Flexbox regelt die Positionierung automatisch basierend auf der tatsächlichen Sidebar-Breite */

/* Hauptinhalt auf der Startseite (ohne Sidebar) */
.main-content.home-page {
  padding: 0;
  margin-left: 0;
}

/* Content-Container für Tools und Admin-Bereiche - maximal 1200px breit */
.main-content > *:not(.home-page-content) {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto; /* Zentriert den Inhalt horizontal */
}

/* Layout ohne Sidebar (für Startseite) */
.no-sidebar {
  max-width: 100%;
  padding: 0; /* Kein Padding auf der Startseite für Fullscreen-Effekt */
}

/* Spezielle Stile für die Startseite */
.home-content {
  padding: 0;
  margin: 0;
  height: 100vh;
  overflow: hidden;
}

/* Home-Layout für die Startseite */
.home-layout {
  height: 100vh;
  margin-top: 0; /* Kein Margin-Top für die Startseite, da der Videohintergrund fixed ist */
  padding-top: calc(var(--header-height) + 48px); /* Stattdessen Padding-Top für den Abstand zum Header */
}

/* Responsive Anpassungen - angepasst für Flexbox-Layout */
@media (max-width: 768px) {
  /* page-wrapper wird auf mobilen Geräten zu einem Stack-Layout */
  .page-wrapper {
    flex-direction: column;
  }
  
  .main-content {
    padding: var(--spacing-sm);
    /* Wichtig: Auf mobilen Geräten immer den gesamten Inhalt anzeigen */
    width: 100%;
    margin-left: 0;
  }
  
  /* Sidebar-Wrapper für mobile Ansicht */
  .sidebar-wrapper {
    /* Mobile Sidebar wird absolut positioniert und blockiert nicht den Hauptinhalt */
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1000;
    /* Wichtig: Beeinflusst nicht das Flexbox-Layout des page-wrapper */
    flex: none;
    width: auto;
    height: auto;
  }
  
  .sidebar {
    /* Mobile Sidebar-Styles bleiben in der Sidebar-Komponente */
    width: min(85vw, var(--sidebar-width));
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
  }
  
  /* Header-Anpassungen für mobile */
  .main-nav {
    padding: 0 var(--spacing-xs);
  }
  
  .main-content {
    padding: 0 var(--spacing-xs);
  }
  
  /* Anpassung der Header-Höhe auf mobilen Geräten */
  :root {
    --header-height: 80px; /* Kleinere Header-Höhe auf mobilen Geräten */
  }
}

/* Mobile Warning Styles */
.mobile-warning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.mobile-warning-modal {
  background-color: var(--color-background, #ffffff);
  border-radius: 12px;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.mobile-warning-content {
  padding: 32px 24px 24px;
  text-align: center;
}

.warning-icon {
  color: #f59e0b;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.warning-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text, #1f2937);
  margin: 0 0 12px 0;
}

.warning-message {
  font-size: 0.95rem;
  color: var(--color-text-light, #6b7280);
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.warning-actions {
  display: flex;
  justify-content: center;
}

.warning-button {
  background-color: var(--color-primary, #006064);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
}

.warning-button:hover {
  background-color: var(--color-primary-dark, #004d40);
  transform: translateY(-1px);
}

.warning-button:focus {
  outline: 2px solid var(--color-primary, #006064);
  outline-offset: 2px;
}

.warning-button:active {
  transform: translateY(0);
}

/* Dark Mode Anpassungen */
:global(.mode-dark) .mobile-warning-modal {
  background-color: var(--color-background, #1f2937);
}

:global(.mode-dark) .warning-title {
  color: var(--color-text, #f9fafb);
}

:global(.mode-dark) .warning-message {
  color: var(--color-text-light, #9ca3af);
}

/* Responsive Anpassungen */
@media (max-width: 480px) {
  .mobile-warning-modal {
    margin: 0 16px;
  }
  
  .mobile-warning-content {
    padding: 24px 20px 20px;
  }
  
  .warning-title {
    font-size: 1.1rem;
  }
  
  .warning-message {
    font-size: 0.9rem;
  }
}

/* Animation */
.mobile-warning-overlay {
  animation: fadeIn 0.3s ease-out;
}

.mobile-warning-modal {
  animation: slideUp 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
